import * as React from 'react';
import { isNil } from 'lodash';

import DatePickerMulti from 'components/date-picker/date-picker-multi/DatePickerMultiLazy';
import DeliverableTag from 'modules/deliverables/components/deliverable-tag/DeliverableTag';
import InputArray from 'components/inputs/input-array/InputArray';
import Input from 'components/inputs/input/Input';
import { FormField } from 'components/form';
import RichTextEditor from 'components/inputs/rich-text-editor/RichTextEditor';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';

import BulkWMCGeneratorForm from '../bulk-wmc-generator-form/BulkWMCGeneratorForm';
import BulkTimelineForm from '../bulk-timeline-form/BulkTimelineForm';
import BulkPostponeForm from '../bulk-postpone-form/BulkPostponeForm';

import { __t, applyTranslations } from 'utils/language';
import {
  DELIVERABLE_ACTION_HASHTAGS,
  DELIVERABLE_ACTION_MENTIONS,
  DELIVERABLE_ACTION_TAGS,
  DELIVERABLE_ADD_REPLACE_ZALANDO_PAGE_ACTION,
  DELIVERABLE_ADD_WMC_LINK_ACTION,
  DELIVERABLE_ADD_ZALANDO_PAGE_ACTION,
  DELIVERABLE_EDIT_ACTIONS,
  DELIVERABLE_EDIT_ACTIONS_ZALANDO_OUTFIT,
  DELIVERABLE_POSTPONE_ACTION,
  DELIVERABLE_REPLACE_COLORED_TAG_ACTION,
  DELIVERABLE_REPLACE_DESCRIPTION_ACTION,
  DELIVERABLE_REPLACE_TIMELINE_ACTION,
  DELIVERABLE_REPLACE_WMC_LINK_ACTION,
  DELIVERABLE_REPLACE_ZALANDO_PAGE_ACTION,
  MAX_DELIVERABLE_DESCRIPTION_LENGTH
} from 'utils/deliverables';
import { isDefined } from 'utils/helper/common';
import { maxLength, minLengthArray } from 'utils/validators';

import './bulk-edit-action-type-form.scss';

import type {
  Deliverable,
  DeliverablePersistedWMCLink,
  DeliverableTag as DeliverableTagAlias,
  MixedSelectDropdownItem,
  TimeframeResult
} from 'types';
import type {
  BulkEditZalandoLinkWmcRequest,
  BulkOfferModalSelectedTimelines,
  BulkOfferModalSubcampaignCustomOffer,
  BulkOfferModalTimelineData
} from 'modules/subcampaigns/types/BulkOfferModal.type';

applyTranslations(require('./bulk-edit-action-type-form.i18n.json'));

type SelectOption = {
  label: string,
  value: string
};

type Data = {
  name: string,
  value: string | Array<string> | TimeframeResult | DeliverableTagAlias | DeliverablePersistedWMCLink | null | {}
};

type DataWmc = {
  name: 'wmcRequest',
  value: BulkEditZalandoLinkWmcRequest
};

type Props = {
  pickedTimelines: BulkOfferModalSelectedTimelines,
  contentTimelines: BulkOfferModalTimelineData,
  groupedDeliverables: Array<BulkOfferModalSubcampaignCustomOffer>,
  selectedDeliverableIds: Array<number>,
  actionType: string,
  days: number,
  hashtags: Array<string>,
  tags: Array<string>,
  mentions: Array<string>,
  timeline: TimeframeResult,
  deliverableTag: DeliverableTagAlias | null,
  description: string,
  contentApprovalV2: boolean,
  isZalandoOutfitOnly: boolean,
  isMultiZLinkEnabled: boolean,
  handleTypeChange: (value: string) => void,
  onChange: (a: Data) => void,
  onWmcChange: (wmcRequest: DataWmc) => void
};

type State = {
  socialTags: Array<string>
};

const MSG_KEY = 'bulk_edit_action_type_form';
const MIN_LENGTH_ARRAY = 1;

const getSelectOptions = (
  isMultiZLinkEnabled: boolean
): Array<SelectOption> => (
  DELIVERABLE_EDIT_ACTIONS
    .filter(type => {
      // hide "Add or replace Zalando tracking link" option
      if (isMultiZLinkEnabled && type === DELIVERABLE_ADD_REPLACE_ZALANDO_PAGE_ACTION) {
        return false;
      }

      // hide "Add Zalando tracking link" and "Replace Zalando tracking link" options
      if (!isMultiZLinkEnabled && (
        type === DELIVERABLE_ADD_ZALANDO_PAGE_ACTION || type === DELIVERABLE_REPLACE_ZALANDO_PAGE_ACTION
      )) {
        return false;
      }

      return true;
    })
    .map(type => ({
      label: __t(`${MSG_KEY}.option_${type}`),
      value: type
    }))
);

class BulkEditActionTypeForm extends React.PureComponent<Props, State> {
  wmcOptions: Array<string> = [
    DELIVERABLE_ADD_REPLACE_ZALANDO_PAGE_ACTION,
    DELIVERABLE_ADD_WMC_LINK_ACTION,
    DELIVERABLE_ADD_ZALANDO_PAGE_ACTION,
    DELIVERABLE_REPLACE_WMC_LINK_ACTION,
    DELIVERABLE_REPLACE_ZALANDO_PAGE_ACTION
  ];

  state = {
    socialTags: []
  };

  getActionItems = (): Array<MixedSelectDropdownItem> => {
    const { actionType, isZalandoOutfitOnly, isMultiZLinkEnabled } = this.props;
    const options = getSelectOptions(isMultiZLinkEnabled);

    return options.map(option => ({
      identifier: option.value,
      isDisabled: isZalandoOutfitOnly && !DELIVERABLE_EDIT_ACTIONS_ZALANDO_OUTFIT.includes(option.value),
      isSelected: option.value === actionType,
      label: option.label,
      value: option.value
    }));
  };

  handleDaysChange = (event: React.SyntheticEvent<HTMLSelectElement>) => {
    this.props.onChange({
      name: 'days',
      value: event.currentTarget.value
    });
  };

  handleValueChange = (name: string) => (items: Array<string>) => {
    this.setState({
      socialTags: items
    });

    this.props.onChange({
      name,
      value: items
    });
  };

  handleTimelineChange = (startDate?: string | null, endDate?: string | null): void => {
    this.props.onChange({
      name: 'timeline',
      value: {
        startDate: startDate ?? '',
        endDate: endDate ?? ''
      }
    });
  };

  handleTagUpdate = ({ tag }: Partial<Deliverable>) => {
    this.props.onChange({
      name: 'deliverableTag',
      value: tag ?? null
    });
  };

  handleDescriptionChange = (value: string): void => {
    this.props.onChange({
      name: 'description',
      value: value ?? ''
    });
  };

  handleWMCRequestChange = (wmcRequest: BulkEditZalandoLinkWmcRequest): void => {
    this.props.onWmcChange({
      name: 'wmcRequest',
      value: {
        primary: wmcRequest.primary,
        secondary: wmcRequest.secondary
      }
    });
  };

  handlePickedTimelineChange = (event: React.SyntheticEvent<HTMLInputElement>): void => {
    const newState = {
      ...this.props.pickedTimelines,
      [event.currentTarget.value]: event.currentTarget.checked ?? false
    };

    this.props.onChange({
      name: 'pickedTimelines',
      value: newState
    });
  };

  handleTimelineDateChange = (
    type: 'contentApproval' | 'goLive' | 'insightsProvision',
    startDate?: string | null,
    endDate?: string | null
  ): void => {
    const { contentTimelines } = this.props;
    const newState = {
      ...contentTimelines,
      [type]: {
        startDate: startDate ?? '',
        endDate: endDate ?? ''
      }
    };

    this.props.onChange({
      name: 'contentTimelines',
      value: newState
    });
  };

  renderActionSelector() {
    const { actionType, handleTypeChange } = this.props;
    const actionItems = this.getActionItems();

    return (
      <FormField
        label={__t(`${MSG_KEY}.label_type_of_action`)}
        labelType="highlighted"
        labelSize="fixed"
        showValidationIcon={false}
        direction="row"
        fontSize="small"
      >
        <MixedSelectDropdown
          withSelectedInList
          name="deliverable-details-edit-tag-color"
          items={actionItems}
          onItemsToggle={(items) => {
            handleTypeChange(items.find(item => item.isSelected)?.value ?? actionType);
          }}
        />
      </FormField>
    );
  }

  renderInputFormField() {
    const {
      actionType,
      days,
      hashtags = [],
      tags = [],
      mentions = [],
      timeline = {
        startDate: '',
        endDate: ''
      },
      contentApprovalV2
    } = this.props;

    if (actionType === DELIVERABLE_POSTPONE_ACTION) {
      return (
        <Input
          name="days"
          type="number"
          min={0}
          width="responsive"
          value={String(days || '')}
          onChange={this.handleDaysChange}
        />
      );
    }

    if (actionType === DELIVERABLE_REPLACE_TIMELINE_ACTION && !contentApprovalV2) {
      return (
        <DatePickerMulti
          startDateName="startDate"
          startDate={timeline.startDate}
          endDateName="endDate"
          endDate={timeline.endDate}
          onChange={this.handleTimelineChange}
        />
      );
    }

    const name = actionType.split('_').pop() ?? '';
    const prefix = name === DELIVERABLE_ACTION_HASHTAGS ? '#' : '@';
    let value: Array<string> = [];

    switch (name) {
      case DELIVERABLE_ACTION_HASHTAGS:
        value = [...hashtags];
        break;
      case DELIVERABLE_ACTION_TAGS:
        value = [...tags];
        break;
      case DELIVERABLE_ACTION_MENTIONS:
        value = [...mentions];
        break;
      default:
        break;
    }

    if (isNil(name)) {
      return null;
    }

    return (
      <InputArray
        name={name}
        prefix={prefix}
        max={30}
        value={value}
        onChange={this.handleValueChange(name)}
      />
    );
  }

  renderForm() {
    const {
      days,
      actionType,
      deliverableTag,
      pickedTimelines,
      contentTimelines,
      contentApprovalV2,
      groupedDeliverables,
      selectedDeliverableIds,
      description = '',
      isZalandoOutfitOnly,
      isMultiZLinkEnabled
    } = this.props;

    const { socialTags } = this.state;

    if (actionType === DELIVERABLE_REPLACE_TIMELINE_ACTION && contentApprovalV2) {
      return (
        <BulkTimelineForm
          pickedTimelines={pickedTimelines}
          contentTimelines={contentTimelines}
          groupedDeliverables={groupedDeliverables}
          selectedDeliverableIds={selectedDeliverableIds}
          isZalandoOutfitOnly={isZalandoOutfitOnly}
          onTimelineSelect={this.handlePickedTimelineChange}
          onDateChange={this.handleTimelineDateChange}
        />
      );
    }

    if (actionType === DELIVERABLE_POSTPONE_ACTION && contentApprovalV2) {
      return (
        <BulkPostponeForm
          pickedTimelines={pickedTimelines}
          days={days}
          groupedDeliverables={groupedDeliverables}
          selectedDeliverableIds={selectedDeliverableIds}
          isZalandoOutfitOnly={isZalandoOutfitOnly}
          onTimelineSelect={this.handlePickedTimelineChange}
          onDaysChange={this.handleDaysChange}
        />
      );
    }

    if (actionType === DELIVERABLE_REPLACE_COLORED_TAG_ACTION) {
      return (
        <div className="bulk-edit-action-type-form__input">
          <DeliverableTag
            deliverableTag={deliverableTag}
            onEdit={this.handleTagUpdate}
            direction="column"
            isModal
          />
        </div>
      );
    }

    if (actionType === DELIVERABLE_REPLACE_DESCRIPTION_ACTION) {
      return (
        <div className="bulk-edit-action-type-form__editor">
          <FormField
            label={__t(`${MSG_KEY}.label_data_replace_description`)}
            labelType="highlighted"
            showValidationIcon={false}
            direction="row"
            fontSize="small"
            validators={[
              maxLength(description, MAX_DELIVERABLE_DESCRIPTION_LENGTH)
            ]}
          >
            <RichTextEditor
              key={description}
              canEdit
              value={description}
              onChange={this.handleDescriptionChange}
              charCount={{
                maxChars: MAX_DELIVERABLE_DESCRIPTION_LENGTH
              }}
            />
          </FormField>
        </div>
      );
    }

    if (this.wmcOptions.includes(actionType)) {
      return (
        <div>
          <BulkWMCGeneratorForm
            actionType={actionType}
            isMultiZLinkEnabled={isMultiZLinkEnabled}
            onChange={this.handleWMCRequestChange}
          />
        </div>
      );
    }

    const isAddingItem = actionType.includes('add_');

    return (
      <div className="bulk-edit-action-type-form__input">
        <FormField
          label={__t(`${MSG_KEY}.label_data_${actionType}`)}
          labelType="highlighted"
          labelSize="fixed"
          showValidationIcon={false}
          direction="row"
          fontSize="small"
          validators={[
            (isAddingItem && minLengthArray(socialTags, MIN_LENGTH_ARRAY, __t(`${MSG_KEY}.label_entered`)))
              || undefined
          ].filter(isDefined)}
        >
          {this.renderInputFormField()}
        </FormField>
      </div>
    );
  }

  render() {
    return (
      <div className="bulk-edit-action-type-form">
        {this.renderActionSelector()}
        {this.renderForm()}
      </div>
    );
  }
}

export default BulkEditActionTypeForm;
